import GoogleAnalytics from './tracker-googleanalytics';
import GoogleTagManager from './tracker-googletagmanager';
import Webtrekk from './tracker-webtrekk';
import Tealium from './tracker-tealium';
import CQAnalytics from './tracker-cqanalytics';
import Callback from './tracker-callback';
import OEWA from './tracker-oewa';
import Piwik from './tracker-piwik';
import GA4 from "./tracker-ga4";

const TRACKERWRAPPER = {
	cb: Callback,
	cq: CQAnalytics,
	ga: GoogleAnalytics,
	gtm: GoogleTagManager,
	oewa: OEWA,
	tl: Tealium,
	wt: Webtrekk,
	pwk: Piwik,
	ga4: GA4,
};

const tracked = [];
const trackerInstances = [];
let stagedTrackers = [];
let analyticsInitialized = false;
let analyticsOptions = {};
let analyticsStarted = false;

export function getAnalyticsOptions() {
	return analyticsOptions;
}

/**
 * @param {boolean} skipFirstTrack - If `true` the first track is skipped
 * @param {boolean|object} ignoreGTM - If `true` ignores all trackers created by GTM or if an object according to the type
 */
function discover(skipFirstTrack = true, ignoreGTM = false) {
	return Promise.all(Object.keys(TRACKERWRAPPER).map(type =>
		TRACKERWRAPPER[type]
			.discover(skipFirstTrack ? (tracked.length || 1) - 1 : -1, typeof ignoreGTM === "boolean" ? ignoreGTM : ignoreGTM[type])
			.then(trackers => {
				trackerInstances.push(...trackers);
			})
	)).then(() => {
		!skipFirstTrack && unspool();
	});
}

/**
 * @param {string} type - the type of tracker that should be created @see TRACKERWRAPPER
 * @param {*} options - the options for the tracker
 */
function create(type, options) {
	const tracker = TRACKERWRAPPER[type];
	if (tracker) {
		return tracker.create(options).then(t => {
			if(t) {
				trackerInstances.push(t);
				unspool();
			}
		});
	} else {
		console.warn(`Tracker of type ${type} is not supported`);
		return Promise.resolve(null);
	}
}

/**
 * @param {array} config - A list of tracker configs
 */
function createTrackers(configList) {
	return Promise.all(configList.map(({type, ...rest}) => create(type, rest)));
}

/**
 * @param {array} config - A list of tracker configs
 */
export function stageTrackers(configList) {
	stagedTrackers = configList;
}

//TODO: find better name
function unspool() {
	trackerInstances.forEach(t => {
		let { trackIndex } = t;
		trackIndex += 1;
		const rest = tracked.slice(trackIndex);
		rest.forEach((tr, idx) => {
			t.track(tr.type, tr.payload, trackIndex + idx);
		});
	})
}

/**
 * @param {"page" | "event"} type - the type of track
 * @param {*} payload - an object containing the data that should be tracked
 */
function track(type, payload) {
	const { skipFirstTrackOnDiscovered = true, ignoreGTM = false } = analyticsOptions;
	const p = analyticsStarted ? discover(skipFirstTrackOnDiscovered, ignoreGTM) : Promise.resolve();
	p.finally(() => {
		tracked.push({
			type,
			payload
		});
		unspool()
	});
}

/**
 * @param {string} url - the url to be tracked
 * @param {*} content - the current player content -> user / publishable
 * @param {string} title - the title to be tracked
 * @param {*} options - the current player options
 */
function trackPage(url, content, title, options) {
	const lastUrl = [...tracked].reverse().find(t => t.type === 'page');
	if (lastUrl && lastUrl.url === url) {
		return;
	}
	track('page', {
		url,
		content,
		title,
		options
	});
}

/**
 * @param {string} category - the type or category of event
 * @param {string} action - the action that triggered the event
 * @param {string} label - a label for the value
 * @param {number} value - a numeric value connected to this event
 * @param {string} transport - can be set to `beacon` in GA for sending events when leaving page
 */
export function trackEvent(category, action, label, value, transport = '') {
	track('event', {
		category,
		action,
		label,
		value,
		transport
	});
}

export function trackShare(method, url) {
	trackEvent('share', method, url);
}

export function trackOutbound(url) {
	trackEvent('outbound', 'click', url, undefined, 'beacon');
}

export function gaEvent(category, action, label, value) {
	trackEvent(category, action, label, value);
}

export function startAnalytics() {
	const { skipFirstTrackOnDiscovered = true, ignoreGTM = false } = analyticsOptions;
	if (analyticsStarted || !analyticsInitialized) { return; }
	analyticsStarted = true;

	Promise.all(Object.keys(TRACKERWRAPPER)
		.filter(k => !!stagedTrackers.find(cfg => cfg.type === k))
		.map(type => TRACKERWRAPPER[type].init()))
	.then(() => discover(skipFirstTrackOnDiscovered, ignoreGTM))
	.then(() => createTrackers(stagedTrackers))
}

function stopAnalytics() {
	tracked.length = 0;
	trackerInstances.forEach(t => {
		t.destroy();
	});
	trackerInstances.length = 0;
	analyticsStarted = false;
}

export function initAnalytics(options = {}) {
	analyticsInitialized = true;
	analyticsOptions = options;
	const {
		trackers = [], 
		hasCookieBanner = false,
	} = options;

	if (trackers.length) {
		stageTrackers(trackers)
	}
	if (!hasCookieBanner) {
		startAnalytics();
	}
}

export default (IS_BROWSER ? trackPage : () => {});

if (IS_BROWSER) {
	document.addEventListener("consentInitialized", ev => {
		if(~ev.currentConsent.indexOf('statistics')) {
			const discoveryTimeout = setTimeout(() => {
				startAnalytics();
			}, 2000);
			document.addEventListener("DOMContentLoaded", () => {
				clearTimeout(discoveryTimeout);
				startAnalytics();
			});
		}
	});
	document.addEventListener("consentChanged", ev => {
		const prevStatistics = !!~ev.previousConsent.indexOf('statistics');
		const statistics = !!~ev.currentConsent.indexOf('statistics');
		if(prevStatistics !== statistics) {
			if (statistics) {
				setTimeout(() => startAnalytics(), 500);
			} else {
				stopAnalytics();
			}
		}
	});
}


// WEBPACK FOOTER //
// ./lib/analytics/analytics.js